import { useMemo, useState } from "react";
import { Badge, Flex, Grid, Section } from "@radix-ui/themes";
import { useAuth0 } from "@auth0/auth0-react";
import { Home03Icon } from "@/v2/assets/icons/Home03Icon";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import {
  DateRange,
  parseDateRange,
} from "@/components/shared/date-range-picker-v2";
import {
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { ExplorePieChart } from "@/v2/features/explore-widgets/ExplorePieChart";
import {
  ExploreOverTime,
  ExploreOverTimeTabs,
  useExploreOverTimeTimeGrouping,
} from "@/v2/features/explore-widgets/ExploreOverTime";
import { HomePageHeaderActions } from "@/v2/features/home/HomePageHeaderActions";
import { HomePageCard } from "@/v2/features/home/HomePageCard";
import {
  PerformanceTrendMetrics,
  QualityTrendMetrics,
  TrendMetricTooltip,
} from "@/v2/features/home/TrendMetrics";
import { mergeFilter } from "@/v2/entities/explore/Explore.filter";
import { buildFiltersFromFilter } from "@/v2/features/explore-filter-menu/explore-filter.utils";
import { ResolutionStatusFormatter } from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";

function QuackResolvedOverTimeTooltip({
  source,
}: {
  source: ExploreInteractionType;
}) {
  return (
    <TrendMetricTooltip
      title={`Quack Resolved ${exploreInteractionTypeToLabel[source].plural} Over Time`}
      description={`Trend of ${exploreInteractionTypeToLabel[
        source
      ].plural.toLowerCase()} successfully resolved by Quack without escalation over time.`}
    />
  );
}

function ResolutionStatusBreakdownTooltip({
  source,
}: {
  source: ExploreInteractionType;
}) {
  return (
    <TrendMetricTooltip
      title="Resolution Status Breakdown"
      description={
        <>
          {`Quack-handled ${exploreInteractionTypeToLabel[
            source
          ].plural.toLowerCase()} by resolution status, excluding `}
          <Badge variant="soft" color="gray" highContrast>
            <ResolutionStatusFormatter value="SKIPPED" />
          </Badge>
          {" and "}
          <Badge variant="soft" color="gray" highContrast>
            <ResolutionStatusFormatter value="NOT_RESOLVED" />
          </Badge>
          {` ${exploreInteractionTypeToLabel[source].plural.toLowerCase()}.`}
        </>
      }
    />
  );
}

function Home() {
  const { user } = useAuth0();
  const { timeGrouping, handleTimeGroupingChange } =
    useExploreOverTimeTimeGrouping();

  const [source, setSource] = useState<ExploreInteractionType>("tickets");
  const [dateRange, setDateRange] = useState<DateRange>({
    type: "preset",
    value: "15d",
  });

  // const { ref, inView } = useVisibleInNearestScrollArea({
  //   mode: "partial",
  //   minRatio: 0.55,
  // });

  const parsedDateRange = useMemo(() => parseDateRange(dateRange), [dateRange]);
  const { dateFrom, dateTo } = parsedDateRange;

  const baseFilter = useMemo(() => {
    if (source === "conversations") {
      return {
        and: [{ subject: { isnotnull: "true" } }],
      };
    }
  }, [dateRange, source]);

  const filter = useMemo(() => {
    const windowFilter = {
      and: [{ createdAt: { gte: dateFrom, lte: dateTo } }],
    };

    if (baseFilter == null) {
      return windowFilter;
    }

    return mergeFilter(baseFilter, windowFilter);
  }, [baseFilter, dateFrom, dateTo]);

  const resolvedFilter = useMemo(() => {
    return mergeFilter(filter, {
      and: [{ resolutionStatus: { in: ["RESOLVED"] } }],
    });
  }, [filter]);

  const resolutionDistributionFilter = useMemo(() => {
    return mergeFilter(filter, {
      and: [
        {
          resolutionStatus: {
            in: ["RESOLVED", "EXPECTED_ESCALATION", "UNEXPECTED_ESCALATION"],
          },
        },
      ],
    });
  }, [filter]);

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          // ref={ref}
          // sticky={!inView}
          title={`Welcome ${user?.name}`}
          description="AI performance overview and analytics"
          icon={<Home03Icon size={24} fill={PageColors[ENavigate.HOME] || "gray"} />}
          actions={
            <HomePageHeaderActions
              source={source}
              dateRange={dateRange}
              setSource={setSource}
              setDateRange={setDateRange}
            />
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <PerformanceTrendMetrics
              source={source}
              dateRange={parsedDateRange}
              filter={baseFilter}
            />

            <Grid
              columns={{
                initial: "1",
                xl: "2",
              }}
              rows={{
                initial: "repeat(2, 480px)",
                xl: "repeat(1, 480px)",
              }}
              gap="3"
              width="100%"
            >
              <HomePageCard
                key={`resolution-status-over-time-${source}`}
                linkProps={{
                  text: `Quack Resolved ${exploreInteractionTypeToLabel[source].plural} Over Time`,
                  source,
                  filters: buildFiltersFromFilter(resolvedFilter),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
                tooltip={<QuackResolvedOverTimeTooltip source={source} />}
                actions={
                  <ExploreOverTimeTabs
                    value={timeGrouping}
                    onValueChange={handleTimeGroupingChange}
                  />
                }
              >
                <Flex className="h-full">
                  <ExploreOverTime
                    source={source}
                    filter={resolutionDistributionFilter}
                    splitBy="resolutionStatus"
                    groupBy={timeGrouping}
                    hideValues={[
                      "unexpected_escalation",
                      "expected_escalation",
                    ]}
                  />
                </Flex>
              </HomePageCard>

              <HomePageCard
                key={`resolution-status-breakdown-${source}`}
                linkProps={{
                  text: "Resolution Status Breakdown",
                  source,
                  groupBy: "resolutionStatus",
                  filters: buildFiltersFromFilter(resolutionDistributionFilter),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
                tooltip={<ResolutionStatusBreakdownTooltip source={source} />}
              >
                <ExplorePieChart
                  source={source}
                  filter={resolutionDistributionFilter}
                  groupBy="resolutionStatus"
                />
              </HomePageCard>
            </Grid>

            <QualityTrendMetrics source={source} dateRange={parsedDateRange} />
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}

export default Home;
